import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { uniqBy } from 'lodash';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterItem from './filter-item';
import { useStringsContext } from '../../../../contexts/strings';
import { TeamConfig, Filter, Field, Operation, FilterOption, FilterType } from '../types';
import { TeamData } from '../../../../types/team';
import { FilterValueOptions } from './filter-item';
import { getFiltersConf, getLeagueImage, GetValueProps } from '../utils';

export function FilterMenuItem(props: {
  label: string;
  options: FilterOption[];
  operation?: Operation;
  selectedOptions: string[];
  open: boolean;
  setOpen: (value: boolean) => void;
  onSelect: (selections: string[], operation: Operation) => void;
  showImage: boolean;
  getValue: (params: GetValueProps) => string[];
}) {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const {
    label,
    options,
    selectedOptions,
    open,
    setOpen,
    onSelect,
    showImage,
    operation,
    getValue,
  } = props;

  return (
    <>
      <MenuItem
        component="div"
        ref={anchorRef}
        onClick={() => {
          setOpen(true);
        }}
        onMouseOver={() => {
          // setOpen(true);
        }}
        sx={{ width: '100%' }}
      >
        {label}

        <Popper
          sx={{ zIndex: 10 }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          placement="right-start"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <FilterValueOptions
                  options={options}
                  selectedOptions={selectedOptions}
                  showImage={showImage}
                  onSelect={onSelect}
                  operation={operation}
                  getValue={getValue}
                />
              </Paper>
            </Grow>
          )}
        </Popper>
      </MenuItem>
    </>
  );
}

function AddFilter(props: {
  removeFilter: (index: number) => void;
  addFilter: (newFilter: Filter) => void;
  updateFilter: (updatedFilter: Partial<Filter>, index: number) => void;
  leagues: FilterOption[];
  teams: FilterOption[];
  open: boolean;
  setOpen: (open: boolean) => void;
  filterIndex?: number;
  selectedFilter?: Filter;
}) {
  const {
    addFilter,
    removeFilter,
    updateFilter,
    open,
    setOpen,
    filterIndex,
    selectedFilter,
    leagues,
    teams,
  } = props;

  const { strings } = useStringsContext();

  const anchorRef = React.useRef<HTMLDivElement>(null);

  const [selectedOption, setSelectedOption] = React.useState<Field | undefined>();

  return (
    <>
      <Box ref={anchorRef}>
        {selectedFilter && filterIndex !== undefined ? (
          <FilterItem
            filter={selectedFilter}
            removeFilter={() => {
              removeFilter(filterIndex);
            }}
            updateFilter={(updatedFilter) => {
              updateFilter(updatedFilter, filterIndex);
            }}
            leagues={props.leagues}
            teams={props.teams}
          />
        ) : (
          <Button
            variant="contained"
            onClick={() => {
              setOpen(!open);
            }}
            startIcon={<FilterListIcon />}
          >
            {strings.allPlayers.filter}
          </Button>
        )}
      </Box>
      <Popper
        sx={{ zIndex: 10, height: '150px' }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        placement="bottom-start"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper sx={{ minWidth: 120, maxWidth: '100%' }}>
              <ClickAwayListener
                onClickAway={() => {
                  setSelectedOption(undefined);
                  setOpen(false);
                }}
              >
                <MenuList sx={{ width: '100%' }}>
                  {getFiltersConf({
                    strings,
                    teams,
                    leagues,
                  }).map((option) => (
                    <FilterMenuItem
                      key={option.field}
                      label={option.label}
                      getValue={option.getValue}
                      operation={selectedFilter?.operation}
                      options={option.options}
                      showImage={option.showImage}
                      selectedOptions={selectedFilter?.value || []}
                      open={selectedOption === option.field}
                      setOpen={(value) => {
                        setSelectedOption(value ? option.field : undefined);
                        if (
                          selectedOption &&
                          selectedOption !== option.field &&
                          selectedFilter &&
                          filterIndex !== undefined
                        ) {
                          updateFilter(
                            {
                              ...selectedFilter,
                              value: [],
                            },
                            filterIndex
                          );
                        }
                      }}
                      onSelect={(selections, operation) => {
                        const updatedFilter = {
                          field: option.field,
                          operation,
                          value: selections,
                          filterType: option.filterType,
                        };
                        if (selectedFilter && filterIndex !== undefined) {
                          updateFilter(updatedFilter, filterIndex);
                        } else {
                          addFilter(updatedFilter);
                        }

                        if (option.filterType !== FilterType.MultipleValues) {
                          setOpen(false);
                        }
                      }}
                    />
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}

export default function Filters(props: {
  filters: Filter[];
  removeFilter: (index: number) => void;
  addFilter: (newFilter: Filter) => void;
  updateFilter: (updatedFilter: Partial<Filter>, index: number) => void;
  teamsConfig: TeamConfig[];
  teams: TeamData[];
}) {
  const { filters, teamsConfig, removeFilter, updateFilter } = props;

  const [open, setOpen] = React.useState(false);
  const [filterIndex, setFilterInder] = React.useState<number | undefined>();
  const selectedFilter = filterIndex !== undefined ? filters[filterIndex] : undefined;

  useEffect(() => {
    setFilterInder(open ? filters.length : undefined);
  }, [open]);

  const leagues = uniqBy(
    teamsConfig
      .map((t) => t.configLeagueName)
      .filter((t) => t !== 'none' || !t)
      .map((value) => ({
        label: value,
        value,
        image: `/leagues/${value}.png`,
        operation: Operation.CS,
      })),
    'value'
  );

  const teams = uniqBy(
    teamsConfig
      .filter((t) => !t || !t.name || t.name !== 'none')
      .map((t) => {
        const team = props.teams.find((team) => team.abbrv === t.abbrv);
        const value = t.configLeagueName + '__' + t.name;
        const subLabel = (() => {
          if (t.configLeagueName.toLowerCase() === 'none') {
            return '';
          }
          return t.configLeagueName || '';
        })();
        return {
          label: t.name,
          subLabel,
          subImage: getLeagueImage(t.configLeagueName),
          value,
          image: team?.logoImageUrl,
          operation: Operation.CS,
        };
      }),
    'value'
  );

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {filters
        .slice(0, selectedFilter && filterIndex !== undefined ? -1 : undefined)
        .map((filter, index) => (
          <React.Fragment key={index}>
            <FilterItem
              filter={filter}
              removeFilter={() => {
                removeFilter(index);
              }}
              updateFilter={(updatedFilter) => {
                updateFilter(updatedFilter, index);
              }}
              leagues={leagues}
              teams={teams}
            />
          </React.Fragment>
        ))}
      <AddFilter
        {...props}
        leagues={leagues}
        teams={teams}
        open={open}
        setOpen={setOpen}
        filterIndex={filterIndex}
        selectedFilter={selectedFilter}
      />
    </Box>
  );
}
